import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../contexts/auth-context";
import { useAsyncFn } from "../../hooks/use-async-fn";
import { lightenColor, lightenColorSoft, tintColor, getContrastColor, hexToRgbChannels } from "../../utils";

type Branding = {
  primary_color: string;
  element_color: string;
  secondary_color: string;
  platform_logo: string;
};

const FederationContext = createContext<{
  fedId: string | null;
  setFedId: (id: string) => void;
  configScope: string | null;
  branding: Branding | null;
  platformLogo: string;
  setPlatformLogo: (logo: string) => void;
}>({
  fedId: null,
  setFedId: () => {},
  configScope: null,
  branding: null,
  platformLogo: null,
  setPlatformLogo: () => {},
});

export const FederationProvider = ({ children }: { children: ReactNode }) => {
  const history = useHistory();
  const { getFednDetails, getApplication } = useContext(AuthContext);
  const [fedId, setFedId] = useState<string | null>(
    localStorage.getItem("federationkey"),
  );
  const [configScope] = useState<string | null>(
    localStorage.getItem("configScope"),
  );
  const [branding, setBranding] = useState<Branding | null>(null);
  const [platformLogo, setPlatformLogo] = useState<string | null>(null);
  const getFednDetailsFn = useAsyncFn({
    deferFn: getFednDetails,
    federationID: fedId,
  });
  const getApplicationFn = useAsyncFn({
    promiseFn: getApplication,
  });

  const updateFedId = (id: string) => {
    localStorage.setItem("federationkey", id);
    setFedId(id);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (getFednDetailsFn.data || getApplicationFn.data) {
      fetchBranding(fedId);
    }
  }, [getFednDetailsFn.data, getApplicationFn.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (fedId != null && fedId !== "null" && fedId !== "") {
      getFednDetailsFn.run({ federationID: fedId });
    }
    if (!fedId) fetchBranding(null);
  }, [fedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBranding = async (fedIdFromStorage: string | null) => {
    if (
      getApplicationFn?.data?.setting?.toLowerCase() ===
        "federation settings" &&
      fedIdFromStorage
    ) {
      try {
        const data = getFednDetailsFn.data && getFednDetailsFn.data;
        setBranding(data?.color_scheme);

        document.documentElement.style.setProperty(
          "--Primary-Color-Shade-1",
          data?.element_color,
        );
        document.documentElement.style.setProperty(
          "--Primary-Color-Shade-1-pale",
          lightenColor(data?.element_color),
        );
        document.documentElement.style.setProperty(
          "--Primary-Color-Shade-1-pale-hover",
          tintColor(data?.element_color, 0.88),
        );
        document.documentElement.style.setProperty(
          "--Primary-Color-Shade-1-rgb",
          hexToRgbChannels(data?.element_color),
        );
        document.documentElement.style.setProperty(
          "--breadcrumb-text-color",
          getContrastColor(data?.element_color),
        );
        document.documentElement.style.setProperty(
          "--active-bg-color",
          data?.primary_color,
        );
        document.documentElement.style.setProperty(
          "--accent-color",
          data?.secondary_color,
        );
        document.documentElement.style.setProperty(
          "--light-primary-color",
          lightenColor(data?.element_color),
        );
        document.documentElement.style.setProperty(
          "--soft-primary-color",
          lightenColorSoft(data?.element_color),
        );
        setPlatformLogo(getFednDetailsFn?.data?.platform_logo);
      } catch (err) {
        console.error("Failed to fetch federation branding", err);
      }
    } else {
      try {
        const data = getApplicationFn?.data?.color_scheme;
        if (!data) return;
        setBranding(data);

        const info_color = lightenColor(data.secondary_color);
        const light_primary_color = lightenColor(data.element_color);
        const soft_primary_color = lightenColorSoft(data.element_color);

        document.documentElement.style.setProperty(
          "--Primary-Color-Shade-1",
          data.element_color,
        );
        document.documentElement.style.setProperty(
          "--Primary-Color-Shade-1-pale",
          lightenColor(data.element_color),
        );
        document.documentElement.style.setProperty(
          "--Primary-Color-Shade-1-pale-hover",
          tintColor(data.element_color, 0.88),
        );
        document.documentElement.style.setProperty(
          "--Primary-Color-Shade-1-rgb",
          hexToRgbChannels(data.element_color),
        );
        document.documentElement.style.setProperty(
          "--breadcrumb-text-color",
          getContrastColor(data.element_color),
        );
        document.documentElement.style.setProperty(
          "--active-bg-color",
          data.primary_color,
        );
        document.documentElement.style.setProperty(
          "--accent-color",
          data.secondary_color,
        );
        document.documentElement.style.setProperty("--info-color", info_color);
        document.documentElement.style.setProperty(
          "--light-primary-color",
          light_primary_color,
        );
        document.documentElement.style.setProperty(
          "--soft-primary-color",
          soft_primary_color,
        );
        setPlatformLogo(getApplicationFn?.data?.logo);
      } catch (err) {
        console.error("Failed to fetch global branding", err);
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const unlisten = history.listen(() => {
      const fed = localStorage.getItem("federationkey");
      fetchBranding(fed);
    });

    return () => {
      unlisten();
    };
  }, [history]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FederationContext.Provider
      value={{
        fedId,
        setFedId: updateFedId,
        configScope,
        branding,
        platformLogo,
        setPlatformLogo,
      }}
    >
      {children}
    </FederationContext.Provider>
  );
};

export const useFederation = () => useContext(FederationContext);
